import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useFilterController } from '../../../../application/useFilterController';

import { applySqftChange } from '../../../../domain/mappers/sqftMapper';
import { applyLotSizeChange } from '../../../../domain/mappers/lotSizeMapper';
import { applyStoriesChange } from '../../../../domain/mappers/storiesMapper';
import { applyYearBuiltChange } from '../../../../domain/mappers/yearBuiltMapper';
import {
  sqftOptionsMin,
  sqftOptionsMax,
  lotSizeOptionsMin,
  lotSizeOptionsMax,
  storiesOptionsMin,
  storiesOptionsMax,
  yearBuiltOptionsMin,
  yearBuiltOptionsMax
} from '../../../../domain/filterOptions';
import { formatSqftValue } from '../../../../domain/mappers/sqftMapper';
import { formatLotSizeValue } from '../../../../domain/mappers/lotSizeMapper';
import { formatStoriesValue } from '../../../../domain/mappers/storiesMapper';
import { formatYearBuiltValue } from '../../../../domain/mappers/yearBuiltMapper';
import { RangeInput } from '../../common/RangeInput';

// Convert mixed type arrays to string arrays
const storiesOptionsMinStrings = storiesOptionsMin.map(option => option.toString());
const storiesOptionsMaxStrings = storiesOptionsMax.map(option => option.toString());

export const PropertyDetailsDrawerSection = () => {
  const {
    sqftRange,
    setSqftRange,
    lotSizeRange,
    setLotSizeRange,
    storiesRange,
    setStoriesRange,
    yearBuiltRange,
    setYearBuiltRange,
  } = useFilterController();
  const [ isMobileOpen, setIsMobileOpen ] = useState<boolean>(false);

  return (
    <div className="p-4">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Property details</h3>
          <button
            type="button"
            className="sm:hidden inline-flex items-center rounded border border-gray-300 p-1 text-gray-600"
            onClick={() => setIsMobileOpen((v) => !v)}
            aria-expanded={isMobileOpen}
            aria-controls="property-details-body"
          >
            {isMobileOpen ? (
              <ChevronUpIcon className="h-5 w-5" />
            ) : (
              <ChevronDownIcon className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      <div id="property-details-body" className={`${isMobileOpen ? 'block' : 'hidden'} sm:block`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <RangeInput
            label="Square feet"
            minValue={formatSqftValue(sqftRange.min, true)}
            maxValue={formatSqftValue(sqftRange.max, false)}
            minOptions={sqftOptionsMin}
            maxOptions={sqftOptionsMax}
            onMinChange={(value) => setSqftRange(applySqftChange(sqftRange, 'min', value))}
            onMaxChange={(value) => setSqftRange(applySqftChange(sqftRange, 'max', value))}
          />

          <RangeInput
            label="Lot size"
            minValue={formatLotSizeValue(lotSizeRange.min, true)}
            maxValue={formatLotSizeValue(lotSizeRange.max, false)}
            minOptions={lotSizeOptionsMin}
            maxOptions={lotSizeOptionsMax}
            onMinChange={(value) => setLotSizeRange(applyLotSizeChange(lotSizeRange, 'min', value))}
            onMaxChange={(value) => setLotSizeRange(applyLotSizeChange(lotSizeRange, 'max', value))}
          />

          <RangeInput
            label="Stories"
            minValue={formatStoriesValue(storiesRange.min, true)}
            maxValue={formatStoriesValue(storiesRange.max, false)}
            minOptions={storiesOptionsMinStrings}
            maxOptions={storiesOptionsMaxStrings}
            onMinChange={(value) => setStoriesRange(applyStoriesChange(storiesRange, 'min', value))}
            onMaxChange={(value) => setStoriesRange(applyStoriesChange(storiesRange, 'max', value))}
          />

          <RangeInput
            label="Year Built"
            minValue={formatYearBuiltValue(yearBuiltRange.min, true)}
            maxValue={formatYearBuiltValue(yearBuiltRange.max, false)}
            minOptions={yearBuiltOptionsMin}
            maxOptions={yearBuiltOptionsMax}
            onMinChange={(value) => setYearBuiltRange(applyYearBuiltChange(yearBuiltRange, 'min', value))}
            onMaxChange={(value) => setYearBuiltRange(applyYearBuiltChange(yearBuiltRange, 'max', value))}
          />
        </div>
      </div>
    </div>
  );
};
